import { z } from '@customerio/hydra-validation';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import { getPushPlatforms } from '@/services/db/delivery/utils';
import { isEmptyString } from '@/utils/string';

/**
 * Resolves the Title / Body / Image / Link a push delivery's Message tab shows,
 * ported from the Ember push `Previewer` (`updateBodyProperties` +
 * `translatedBodyJson` + `getPayloadValue`).
 *
 * A "custom payload" push stores per-platform payloads under `android` / `ios`
 * keys in `detail.body` (APNs / FCM JSON), so the real content is nested. A
 * "simple push" stores the rendered content flat on the detail. `resolvePushPreview`
 * resolves each platform's fields via the same `PAYLOAD_PATHS` so the Message tab
 * can switch between them (Ember's device picker); anything that isn't a custom
 * payload (non-JSON body, JSON without platform content) degrades to the flat
 * `subject` / `body` / `image_url` / `url` fields.
 */

type Platform = 'android' | 'ios';
type PayloadKey = 'title' | 'body' | 'image' | 'link';

// PLATFORMS order is load-bearing: it is Ember's `firstValidPlatform` tie-break
// (android before ios) when both platforms carry content.
const PLATFORMS: Platform[] = ['android', 'ios'];

export type PushPreviewFields = { title: string; body: string; image: string; link: string };

/** A dotted-path list, or (iOS image/link) a resolver gated on the APNs payload. */
type PathResolver = string[] | ((payload: Record<string, unknown>) => string[] | null);

// APNs payloads: https://developer.apple.com/library/archive/documentation/NetworkingInternet/Conceptual/RemoteNotificationsPG/CreatingtheNotificationPayload.html
// FCM payloads: https://firebase.google.com/docs/reference/fcm/rest/v1/projects.messages
const PAYLOAD_PATHS: Record<PayloadKey, Record<Platform, PathResolver>> = {
  title: {
    ios: ['message.notification.title', 'message.apns.payload.aps.alert.title', 'aps.alert.title'],
    android: [
      'message.android.notification.title',
      'message.notification.title',
      'message.android.data.title',
      'message.data.title',
    ],
  },
  body: {
    ios: [
      'message.notification.body',
      'message.apns.payload.aps.alert.body',
      'message.apns.payload.aps.alert',
      'aps.alert.body',
      'aps.alert',
    ],
    android: [
      'message.android.notification.body',
      'message.notification.body',
      'message.android.data.body',
      'message.data.body',
    ],
  },
  image: {
    ios: (payload) => (hasMutableContent(payload) ? IOS_IMAGE_PATHS : null),
    android: [
      'message.android.data.image',
      'message.data.image',
      'message.android.data.image_url',
      'message.data.image_url',
    ],
  },
  link: {
    ios: (payload) => (hasMutableContent(payload) ? IOS_LINK_PATHS : null),
    android: ['message.android.data.link', 'message.data.link'],
  },
};

const IOS_IMAGE_PATHS = ['image-url', 'image_url', 'imageUrl', 'CIO.push.image', 'message.apns.payload.CIO.push.image'];
const IOS_LINK_PATHS = ['CIO.push.link', 'message.apns.payload.CIO.push.link'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Walk a dotted path over nested objects (Ember `get`); undefined if any hop is missing. */
function getByPath(source: Record<string, unknown>, path: string): unknown {
  let current: unknown = source;
  for (const segment of path.split('.')) {
    if (!isRecord(current)) {
      return undefined;
    }
    // Read-only traversal along the fixed PAYLOAD_PATHS constants (never user input); the value is assigned
    // to a local, never written into an object, so no prototype can be polluted.
    current = current[segment]; // nosemgrep
  }
  return current;
}

/** iOS image/link only resolve when the payload's `aps` sets `mutable-content` (Ember `getAps`). */
function hasMutableContent(payload: Record<string, unknown>): boolean {
  const viaMessage = getByPath(payload, 'message.apns.payload.aps');
  const aps = viaMessage === undefined ? getByPath(payload, 'aps') : viaMessage;
  return isRecord(aps) && aps['mutable-content'] != null;
}

/** First path whose value is a string (Ember `getPayloadValue`); '' when none match. */
function getPayloadValue(payload: Record<string, unknown>, platform: Platform, key: PayloadKey): string {
  const resolver = PAYLOAD_PATHS[key][platform];
  const paths = typeof resolver === 'function' ? resolver(payload) : resolver;
  if (paths == null) {
    return '';
  }
  for (const path of paths) {
    const value = getByPath(payload, path);
    if (typeof value === 'string') {
      return value;
    }
  }
  return '';
}

// A platform payload is either an object or (legacy) a JSON string of one.
const PlatformPayloadSchema = z.union([z.string(), z.record(z.string(), z.unknown())]).optional();
const CustomPayloadSchema = z.object({ android: PlatformPayloadSchema, ios: PlatformPayloadSchema });

/** A platform's payload as a non-empty object, or null when absent / empty / unparseable. */
function toPlatformPayload(raw: string | Record<string, unknown> | undefined): Record<string, unknown> | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return isRecord(value) && Object.keys(value).length > 0 ? value : null;
}

export type { Platform };

/**
 * A push preview resolved per platform. `platformsWithBody` lists (in `PLATFORMS`
 * order) the platforms a custom payload carries non-empty content for — empty for
 * a simple push or a body without platform content. `byPlatform` holds each
 * platform's resolved fields; `fallback` is the flat simple-push content.
 */
export interface PushPreview {
  platformsWithBody: Platform[];
  byPlatform: Partial<Record<Platform, PushPreviewFields>>;
  fallback: PushPreviewFields;
  /** The body parsed as JSON — Ember's `hasCustomPayload` (a simple push has a plain-text body). */
  isCustomPayload: boolean;
  /** Any platform carries a non-empty title/body/image/link — Ember's `messageHasPreviewKeys`. */
  hasPreviewKeys: boolean;
}

/**
 * Ember `hasCustomPayload`: the body parses as JSON at all — the shape doesn't matter, and
 * an empty body is parsed as `{}` (Ember's `JSON.parse(bodyJson || '{}')`). A simple push
 * stores rendered plain text, which throws.
 *
 * Deliberately not `message-json`'s `isJsonBody`: that one treats an empty body as non-JSON,
 * which is the opposite of what Ember's push previewer does with it.
 */
function hasCustomPayloadBody(body: string): boolean {
  try {
    JSON.parse(body.length === 0 ? '{}' : body);
    return true;
  } catch {
    return false;
  }
}

/** Parse a body as a `{ android, ios }` custom payload, or null when it isn't JSON / that shape. */
function parseCustomPayload(body: string): z.infer<typeof CustomPayloadSchema> | null {
  let data: unknown;
  try {
    data = JSON.parse(body);
  } catch {
    return null;
  }
  const parsed = CustomPayloadSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

function fieldsFor(payload: Record<string, unknown>, platform: Platform): PushPreviewFields {
  return {
    title: getPayloadValue(payload, platform, 'title'),
    body: getPayloadValue(payload, platform, 'body'),
    image: getPayloadValue(payload, platform, 'image'),
    link: getPayloadValue(payload, platform, 'link'),
  };
}

/**
 * Resolve the push Message tab's content per platform (Ember `translatedBodyJson`
 * + `bodyMeta`). A custom payload with both platform bodies is switchable in the
 * UI; a single-platform or simple push resolves to one field set.
 */
export function resolvePushPreview(detail: DeliveryDetail): PushPreview {
  const fallback: PushPreviewFields = {
    title: detail.subject,
    body: detail.body,
    image: detail.image_url,
    link: detail.url,
  };

  const isCustomPayload = hasCustomPayloadBody(detail.body);
  const parsed = parseCustomPayload(detail.body);
  if (parsed == null) {
    return { platformsWithBody: [], byPlatform: {}, fallback, isCustomPayload, hasPreviewKeys: false };
  }

  const platformsWithBody: Platform[] = [];
  const byPlatform: Partial<Record<Platform, PushPreviewFields>> = {};
  for (const platform of PLATFORMS) {
    const payload = toPlatformPayload(parsed[platform]);
    if (payload != null) {
      platformsWithBody.push(platform);
      byPlatform[platform] = fieldsFor(payload, platform);
    }
  }

  const hasPreviewKeys = Object.values(byPlatform).some((fields) =>
    [fields.title, fields.body, fields.image, fields.link].some((value) => !isEmptyString(value)),
  );

  return { platformsWithBody, byPlatform, fallback, isCustomPayload, hasPreviewKeys };
}

/**
 * `platformsWithBody` reordered so the delivery's first sent platform leads, else
 * left in `PLATFORMS` order. Both the Message tab's initial platform and the header
 * title read from this, so the header shows the same platform's title the preview opens on.
 */
export function orderedPushPlatforms(delivery: Delivery, platforms: Platform[]): Platform[] {
  const firstSent = getPushPlatforms(delivery)[0];
  const initial = platforms.find((platform) => platform === firstSent);
  return initial == null ? platforms : [initial, ...platforms.filter((platform) => platform !== initial)];
}

/**
 * The Title / Body / Image / Link for a single-platform view of the payload —
 * the first platform carrying content (Ember `firstValidPlatform`, android-first),
 * or the flat simple-push fields when this isn't a multi-platform custom payload.
 */
export function resolvePushPreviewFields(detail: DeliveryDetail): PushPreviewFields {
  const preview = resolvePushPreview(detail);
  const first = preview.platformsWithBody[0];
  return first != null ? (preview.byPlatform[first] ?? preview.fallback) : preview.fallback;
}
